// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useTranslation from "@/hooks/useTranslation";
import type { TyOrderStatus, TyOrderType } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    type: TyOrderType;
    status: TyOrderStatus;
    /** Lets a step be tapped to move the order there. Absent makes the trail a picture, not a control. */
    onPick?: (status: TyOrderStatus) => void;
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Where an order has got to, drawn from its own kind's path — the same trail the customer is looking
 * at in the menu, so the counter and the person waiting are reading one picture.
 *
 * A delivery never shows `ready` and a table never shows `delivering`, because the path comes from
 * `orderStatusFlow` rather than from a list written out here. An order that was called off has no
 * path left to draw, so it says so instead.
 */
export default function OrderTimeline({ type, status, onPick, disabled }: Props) {
    const t = useTranslation();

    const isAborted = constants.orderAbortStatuses.includes(status as "rejected" | "cancelled");
    const flow = constants.orderStatusFlow[type] ?? [];
    const at = flow.indexOf(status);

    if (isAborted) {
        return (
            <div className="order-timeline order-timeline--off">
                <span className="order-timeline-off-dot" aria-hidden="true" />
                <span className="order-timeline-off-label">{t.orders.status[status]}</span>
            </div>
        );
    }

    return (
        <div className="order-timeline">
            {flow.map((step, index) => {
                const state = index < at ? "done" : index === at ? "now" : "next";
                return (
                    <button
                        key={step}
                        type="button"
                        className="order-timeline-step"
                        data-state={state}
                        disabled={!onPick || disabled || index === at}
                        onClick={() => onPick?.(step as TyOrderStatus)}
                    >
                        <span className="order-timeline-dot" aria-hidden="true" />
                        <span className="order-timeline-label">{t.orders.status[step as TyOrderStatus]}</span>
                    </button>
                );
            })}
        </div>
    );
}
